import { useQueryClient } from "@tanstack/react-query";
import { ArrowUpIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath } from "react-router-dom";
import { MemoEmptyState } from "@/components/MemoEmptyState";
import { userServiceClient } from "@/connect";
import { useView } from "@/contexts/ViewContext";
import { DEFAULT_LIST_MEMOS_PAGE_SIZE } from "@/helpers/consts";
import { useInfiniteMemos } from "@/hooks/useJournalQueries";
import { userKeys } from "@/hooks/useUserQueries";
import { Routes } from "@/router";
import { State } from "@/types/proto/api/v1/common_pb";
import type { Memo } from "@/types/proto/api/v1/memo_service_pb";
import { useTranslate } from "@/utils/i18n";
import JournalEditor from "../JournalEditor";
import JournalFilters from "../JournalFilters";
import type { MemoRenderContext } from "../MasonryView";
import MasonryView from "../MasonryView";
import Skeleton from "../Skeleton";

interface Props {
  renderer: (memo: Memo, context?: MemoRenderContext) => JSX.Element;
  listSort?: (list: Memo[]) => Memo[];
  state?: State;
  orderBy?: string;
  filter?: string;
  pageSize?: number;
  showCreator?: boolean;
  includeAiAnalysis?: boolean;
  hideEditor?: boolean; // If true, don't render editor (editor is managed externally)
  hideFilters?: boolean; // If true, don't render filters (filters are managed externally)
}

function useAutoFetchWhenNotScrollable({
  hasNextPage,
  isFetchingNextPage,
  memoCount,
  onFetchNext,
}: {
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  memoCount: number;
  onFetchNext: () => Promise<unknown>;
}) {
  const autoFetchTimeoutRef = useRef<number | null>(null);

  const isPageScrollable = useCallback(() => {
    const documentHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    return documentHeight > window.innerHeight + 100;
  }, []);

  const checkAndFetchIfNeeded = useCallback(async () => {
    if (autoFetchTimeoutRef.current) {
      clearTimeout(autoFetchTimeoutRef.current);
    }

    await new Promise((resolve) => setTimeout(resolve, 200));

    const scrollable = isPageScrollable();
    const shouldFetch = !scrollable && hasNextPage && !isFetchingNextPage && memoCount > 0;

    if (shouldFetch) {
      await onFetchNext();

      autoFetchTimeoutRef.current = window.setTimeout(() => {
        void checkAndFetchIfNeeded();
      }, 500);
    }
  }, [hasNextPage, isFetchingNextPage, memoCount, isPageScrollable, onFetchNext]);

  useEffect(() => {
    if (!isFetchingNextPage && memoCount > 0) {
      void checkAndFetchIfNeeded();
    }
  }, [memoCount, isFetchingNextPage, checkAndFetchIfNeeded]);

  useEffect(() => {
    return () => {
      if (autoFetchTimeoutRef.current) {
        clearTimeout(autoFetchTimeoutRef.current);
      }
    };
  }, []);
}

const PagedMemoList = (props: Props) => {
  const { layout } = useView();
  const queryClient = useQueryClient();

  // Show memo editor only on the root route and when not hidden externally
  const showMemoEditor = !props.hideEditor && Boolean(matchPath(Routes.ROOT, window.location.pathname));

  // Editor session management: forces complete remount when canceling
  // This ensures editor state (including height) is fully reset
  const [editorSessionId, setEditorSessionId] = useState(0);

  const handleEditorCancel = useCallback(() => {
    // Increment session ID to force a complete remount of the editor
    const newSessionId = editorSessionId + 1;
    setEditorSessionId(newSessionId);
  }, [editorSessionId]);

  const handleEditorConfirm = useCallback(
    (_memoId: string, aiAnalysis?: { tree: unknown; completed: boolean }) => {
      // For non-AI saves, increment session ID to close the editor
      // AI saves keep the editor open to show results
      if (!aiAnalysis?.completed) {
        const newSessionId = editorSessionId + 1;
        setEditorSessionId(newSessionId);
      }
    },
    [editorSessionId],
  );

  // Generate cache key with session ID to force fresh state
  const editorCacheKey = `home-memo-editor-${editorSessionId}`;

  // Use React Query's infinite query for pagination
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteMemos({
    state: props.state || State.NORMAL,
    orderBy: props.orderBy || "display_time desc",
    filter: props.filter,
    pageSize: props.pageSize || DEFAULT_LIST_MEMOS_PAGE_SIZE,
    includeAiAnalysis: props.includeAiAnalysis ?? false,
  });

  // Flatten pages into a single array of memos
  const memos = useMemo(() => data?.pages.flatMap((page) => page.memos) || [], [data]);

  // Apply custom sorting if provided, otherwise use memos directly
  const sortedMemoList = useMemo(() => (props.listSort ? props.listSort(memos) : memos), [memos, props.listSort]);

  // Track fetchNextPage calls
  const fetchCount = useRef(0);
  const isFetchingManually = useRef(false); // Local flag to prevent duplicate fetch calls
  const lastFetchRequestId = useRef(0); // Track which fetch request is currently active

  const trackedFetchNextPage = useCallback(async () => {
    // Prevent duplicate calls using local flag (React state is too slow)
    if (isFetchingManually.current) {
      return;
    }

    // Additional safeguard: check React Query's state
    if (isFetchingNextPage) {
      return;
    }

    isFetchingManually.current = true;
    lastFetchRequestId.current = fetchCount.current;
    fetchCount.current += 1;

    // Don't set flag in finally, reset it via useEffect when isFetchingNextPage becomes false
    await fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, sortedMemoList.length]);

  // Reset the manual fetch flag when React Query confirms fetch is not in progress
  useEffect(() => {
    if (!isFetchingNextPage && isFetchingManually.current) {
      isFetchingManually.current = false;
    }
  }, [isFetchingNextPage]);

  // Prefetch creators when new data arrives to improve performance
  useEffect(() => {
    if (!data?.pages || !props.showCreator) return;

    const lastPage = data.pages[data.pages.length - 1];
    if (!lastPage?.memos) return;

    const uniqueCreators = Array.from(new Set(lastPage.memos.map((memo) => memo.creator)));
    for (const creator of uniqueCreators) {
      void queryClient.prefetchQuery({
        queryKey: userKeys.detail(creator),
        queryFn: async () => {
          const user = await userServiceClient.getUser({ name: creator });
          return user;
        },
        staleTime: 1000 * 60 * 5,
      });
    }
  }, [data?.pages, props.showCreator, queryClient]);

  // Auto-fetch hook: fetches more content when page isn't scrollable
  useAutoFetchWhenNotScrollable({
    hasNextPage,
    isFetchingNextPage,
    memoCount: sortedMemoList.length,
    onFetchNext: trackedFetchNextPage,
  });

  // Infinite scroll: fetch more when user scrolls near bottom
  useEffect(() => {
    if (!hasNextPage) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const bodyHeight = document.body.offsetHeight;
      const nearBottom = windowHeight + scrollY >= bodyHeight - 300;

      if (nearBottom && !isFetchingNextPage) {
        void trackedFetchNextPage();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage, trackedFetchNextPage]);

  const children = (
    <div className="flex flex-col justify-start items-start w-full max-w-full">
      {/* Show skeleton loader during initial load */}
      {isLoading ? (
        <Skeleton showCreator={props.showCreator} count={4} />
      ) : (
        <>
          <MasonryView
            memoList={sortedMemoList}
            renderer={props.renderer}
            prefixElement={
              !props.hideEditor && showMemoEditor ? (
                <JournalEditor
                  key={editorCacheKey}
                  className="mb-2"
                  cacheKey={editorCacheKey}
                  placeholder="Any thoughts..."
                  onCancel={handleEditorCancel}
                  onConfirm={handleEditorConfirm}
                />
              ) : undefined
            }
            listMode={layout === "LIST"}
          />

          {/* Journal filters - only render if not hidden externally */}
          {!props.hideFilters && <JournalFilters />}

          {/* Loading indicator for pagination */}
          {isFetchingNextPage && <Skeleton showCreator={props.showCreator} count={2} />}

          {/* Empty state */}
          {!isFetchingNextPage && !hasNextPage && sortedMemoList.length === 0 && <MemoEmptyState />}

          {/* Floating back-to-top button */}
          <BackToTop />
        </>
      )}
    </div>
  );

  return children;
};

const BackToTop = () => {
  const t = useTranslate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const shouldShow = window.scrollY > 500;
      setIsVisible(shouldShow);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 h-10 bg-card hover:bg-card/90 border border-border shadow-lg hover:shadow-xl rounded-full text-sm font-medium transition-all duration-300 hover:scale-105 animate-in fade-in slide-in-from-bottom-4"
    >
      <span className="text-foreground/80">{t("router.back-to-top")}</span>
      <ArrowUpIcon className="w-4 h-4 text-muted-foreground" />
    </button>
  );
};

export default PagedMemoList;
